import {
  Copyright,
  Edit,
  Feed,
  Home,
  Note,
  Print,
  ReplyAll,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { assets } from "../../../../../assets";
import ForReconsiderationFileSend from "../../../../../components/common-content/for-reconsideration/ForReconsiderationFileSend";
import IconBreadcrumbs from "../../../../../components/custom/IconBreadcrumbs";
import Loader from "../../../../../components/custom/Loader";
import UpdateCertificate from "../../../../../components/updated-certificate/UpdateCertificate";
import { useAuthorized } from "../../../../../hooks/useAuthorized";
import { useGetCertificateQRCodeQuery } from "../../../../../redux/api/dashboardApiSlice";
import {
  useCertificateEditPermissionMutation,
  useShowCertificateQuery,
} from "../../../../../redux/api/submittedCopyrightAgreementApiSlice";
import { accessRoles } from "../../../../../utils/accessRoles";
import { enLocalDateFormat } from "../../../../../utils/dateFormat";
import CertificateOfBroadcast from "../../../../frontend/certificate/CertificateOfBroadcast";
import CertificateOfCopyright from "../../../../frontend/certificate/CertificateOfCopyright";
import CertificateOfRelatedRight from "../../../../frontend/certificate/CertificateOfRelatedRight";
import LicenseCertificate from "../../../../frontend/certificate/LicenseCertificate";

const PrintCopyrightCertificate = () => {
  const printRef = useRef();
  const { id, user_id } = useParams();
  const { data, isLoading, refetch } = useShowCertificateQuery({ id, user_id });
  const navigate = useNavigate();
  const { isLoading: isLoadingAuthorized, hasRole } = useAuthorized();
  const [editPermission, setEditPermission] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const [openFileTransfer, setOpenFileTransfer] = useState(false);

  const { data: qr_code } = useGetCertificateQRCodeQuery(
    { profileId: id, user_id },
    { skip: !id }
  );

  const [updateCertificatePermission, { isSuccess, data: dataPermission }] =
    useCertificateEditPermissionMutation();

  const ac = data?.data?.archive;
  const pd = data?.data?.profileDetail;

  const publicationDate = () => {
    if (Number(pd?.certificate) === 1) {
      if (Number(pd?.workPublishtype) === 1) {
        if (ac?.publish_year) {
          return ac.publish_year;
        } else {
          return pd?.publish?.year;
        }
      } else {
        return "Unpublished";
      }
    } else {
      if (Number(pd?.pub_unpub) === 1) {
        return pd?.pub_unpub_year;
      } else {
        return "Unpublished";
      }
    }
  };

  const handleAuthor = () => {
    const wn_option = ac?.wn_option ? ` (${ac?.wn_option})` : "";
    if (Number(pd?.certificate) === 1) {
      if (ac?.writers_name) {
        return ac?.writers_name + wn_option;
      } else {
        return (
          pd?.writerDetails.map((item, index) =>
            index === pd.writerDetails.length - 1
              ? item.writerName
              : item.writerName + ", "
          ) + wn_option
        );
      }
    } else {
      return pd?.certificateLangs
        ?.map((item, index) => {
          // Conditionally append ac_option if it exists
          const acOptionText = item?.ac_option ? ` (${item?.ac_option})` : "";

          // Determine if this is the last item in the array
          const separator =
            index === pd.certificateLangs.length - 1 ? "" : ", ";

          return item.author_creator + acOptionText + separator;
        })
        .join(""); // Join the array to create a single string
    }
  };

  const handleOwner = () => {
    const cw_option = ac?.cw_option ? ` (${ac?.cw_option})` : "";
    if (Number(pd?.certificate) === 1) {
      return ac?.cw_proportion + cw_option;
    } else {
      return pd?.certificateLangs.map((item, index) => {
        const orOptionText = item?.or_option ? ` (${item?.or_option})` : "";

        // Determine if this is the last item in the array
        const separator = index === pd.certificateLangs.length - 1 ? "" : ", ";

        return item.owner_right + orOptionText + separator;
      });
    }
  };
  const handleFieldWork = () => {
    if (ac?.creative_work) {
      return ac?.creative_work;
    } else {
      const category =
        pd?.category?.name +
        " (" +
        (pd?.otherSubCategory ? pd?.otherSubCategory : pd?.subCategory?.name) +
        ")";
      return category;
    }
  };

  const handleSign = () => {
    if (ac?.edit_permission !== 1 && ac?.authorizer) {
      return ac.authorizer.sign;
    } else {
      return assets.sign;
    }
  };

  const certificateData = {
    // heading
    publication_date: publicationDate(),
    registration_no: ac?.registration_no,
    date_of_reg: enLocalDateFormat(ac?.date_of_reg),
    validity: ac?.expiry_year
      ? ac?.life_plus === 1
        ? `Life + ${ac.expiry_year} ${ac.expiry_year > 1 ? "Years" : "Year"}`
        : `${ac.expiry_year} ${ac.expiry_year > 1 ? "Years" : "Year"}`
      : enLocalDateFormat(ac?.expiry_date),

    // body
    title: Number(pd?.certificate) === 1 ? pd?.workTitle : pd?.work_title,
    author: handleAuthor(),
    owner: handleOwner(),
    owner_gain: ac?.ownergain,
    field_work: handleFieldWork(),

    // only for license
    tc_license: ac?.tc_licence,

    // footer
    attention: ac?.attention,
    sign: handleSign(),
    imageArtistic:
      pd?.category?.id === 3 && pd?.workSoftCopies.length > 0
        ? pd?.workSoftCopies[0].workSoftCopy
        : "",
    certificate_type: ac?.certificate_type,
  };

  const handleBack = () => {
    navigate(-1);
  };

  const breadcrumbsData = [
    { icon: Home, title: "হোম", url: "/" },
    {
      icon: Copyright,
      title: "কপিরাইট - সার্টিফিকেট",
    },
    {
      icon: Note,
      title: "কপিরাইট রেজিস্ট্রেশন সনদ",
      active: true,
    },
  ];

  const handlePermissionsChange = (e) => {
    const val = e.target.checked ? 1 : 2;
    setEditPermission(val);
    const formData = {
      edit_permission: val,
    };
    updateCertificatePermission({ id, formData }).then(() => {
      refetch();
    });
  };

  useEffect(() => {
    if (ac?.edit_permission) {
      setEditPermission(ac.edit_permission);
    }
  }, [ac?.edit_permission]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(dataPermission?.message);
    }
  }, [isSuccess]);

  if (isLoading || isLoadingAuthorized) {
    return <Loader />;
  }

  return (
    <>
      {data?.data ? (
        <>
          <div className="py-3">
            <IconBreadcrumbs breadcrumbs={breadcrumbsData} />
          </div>
          <div className="dashboard-layout slideUp">
            <div className="py-4 text-center">
              <span>
                <Card variant="outlined">
                  <div className="d-flex p-2 gap-2">
                    <div className="cursor-pointer">
                      <ReactToPrint
                        trigger={() => (
                          <div>
                            <Print /> Print
                          </div>
                        )}
                        documentTitle="certificate"
                        content={() => printRef.current}
                      />
                    </div>
                    {hasRole([accessRoles.Calligrapher]) &&
                      ac?.edit_permission === 1 && (
                        <>
                          <span className="border"></span>
                          <div
                            className="cursor-pointer"
                            onClick={() => setEditOpen(true)}
                          >
                            <Edit />
                            Edit
                          </div>
                        </>
                      )}
                    {hasRole([accessRoles.Register]) && (
                      <>
                        <span className="border"></span>
                        <div className="cursor-pointer">
                          <Form.Check
                            type="switch"
                            id="custom-switch"
                            value={editPermission}
                            label={
                              editPermission === 1
                                ? "সম্পাদনার অনুমতি চালু"
                                : "সম্পাদনার অনুমতি বন্ধ"
                            }
                            checked={editPermission === 1}
                            onChange={handlePermissionsChange}
                          />
                        </div>
                      </>
                    )}
                    <span className="border"></span>
                    <div
                      className="cursor-pointer"
                      onClick={() => setOpenFileTransfer(true)}
                    >
                      <Feed />
                      পুনঃ পরীক্ষার জন্য প্রেরণ
                    </div>

                    <span className="border"></span>
                    <div className="cursor-pointer" onClick={handleBack}>
                      <ReplyAll />
                      Back
                    </div>
                  </div>
                </Card>
              </span>
            </div>
            <div className="pb-5">
              <div ref={printRef}>
                {ac?.certificate_type === 1 ? (
                  <CertificateOfCopyright
                    data={certificateData}
                    qr_code={qr_code?.qr_code}
                  />
                ) : ac?.certificate_type === 2 ? (
                  <LicenseCertificate data={certificateData} />
                ) : ac?.certificate_type === 3 ? (
                  <CertificateOfRelatedRight
                    data={certificateData}
                    qr_code={qr_code?.qr_code}
                  />
                ) : (
                  <CertificateOfBroadcast data={certificateData} />
                )}
              </div>
            </div>
          </div>
          {editOpen && (
            <UpdateCertificate
              refetch={refetch}
              open={editOpen}
              data={data?.data}
              onHide={() => setEditOpen(false)}
            />
          )}

          <Modal show={openFileTransfer}>
            <Modal.Header>পুনঃ পরীক্ষার জন্য প্রেরণ</Modal.Header>
            <Modal.Body>
              <div className="border">
                <ForReconsiderationFileSend data={data?.data} />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setOpenFileTransfer(false)}>Close</Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <>no data found</>
      )}
    </>
  );
};

export default PrintCopyrightCertificate;
